import React from 'react';
import { Modal, Spin } from 'antd';
import { AlertTriangle, Check, Trash2, User } from 'lucide-react';

const DeleteStructureConfirmModal = ({
  visible,
  type,
  positionName,
  affectedMembers,
  loading,
  isDark,
  onCancel,
  onConfirm,
}) => {
  const hasAffected = affectedMembers.length > 0;
  const isLevel = type === 'level';

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered
      closable={false}
      width={460}
      styles={{
        content: {
          background: isDark ? '#0f1117' : '#ffffff',
          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: '20px',
          padding: '0',
          overflow: 'hidden',
          boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.6)' : '0 25px 50px rgba(0,0,0,0.12)',
        },
        mask: { backdropFilter: 'blur(4px)', background: 'rgba(0,0,0,0.45)' }
      }}
    >
      {/* Red top accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-red-500 via-rose-500 to-red-400" />

      <div className="px-7 pt-6 pb-7 flex flex-col gap-5">
        {/* Icon + heading */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <Trash2 size={20} className="text-red-500" strokeWidth={2.2} />
          </div>
          <div className="pt-0.5">
            <h3 className={`text-[15px] font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Delete {isLevel ? 'Level' : 'Position'}?
            </h3>
            <p className={`text-[13px] mt-1 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              You're about to delete{' '}
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                "{positionName}"
              </span>.
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className={`h-px w-full ${isDark ? 'bg-white/[0.06]' : 'bg-gray-100'}`} />

        {/* Affected members section */}
        {loading ? (
          <div className="flex items-center justify-center py-3 gap-2">
            <Spin size="small" />
            <span className={`text-[13px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Checking assigned members...
            </span>
          </div>
        ) : hasAffected ? (
          <div className={`rounded-xl border p-4 ${isDark ? 'bg-red-500/5 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={15} className="text-red-500" strokeWidth={2.2} />
              <p className={`text-[13px] font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                {affectedMembers.length} member{affectedMembers.length > 1 ? 's' : ''} will be affected
              </p>
            </div>
            <p className={`text-[12px] mb-3 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              These members are currently assigned to this {isLevel ? 'level' : 'position'}. Their position will become invalid after deletion.
            </p>
            <div className="flex flex-col gap-1.5 max-h-36 overflow-y-auto">
              {affectedMembers.map(member => (
                <div
                  key={member._id}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-red-500/20 flex items-center justify-center">
                    <User size={13} className="text-red-400" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-[12px] font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {member.name}
                    </p>
                    <p className={`text-[11px] truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {member.email}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={`rounded-xl border p-3.5 flex items-center gap-3 ${isDark ? 'bg-green-500/5 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
            <Check size={15} className="text-green-500 flex-shrink-0" strokeWidth={2.5} />
            <p className={`text-[13px] ${isDark ? 'text-green-400' : 'text-green-600'}`}>
              No members are currently assigned to this {isLevel ? 'level' : 'position'}.
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2.5">
          <button
            onClick={onCancel}
            className={`flex-1 px-4 py-2.5 rounded-xl text-[13px] font-medium border transition-all ${
              isDark
                ? 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold bg-red-600 hover:bg-red-700 !text-white border border-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasAffected ? 'Delete Anyway' : 'Delete'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteStructureConfirmModal;
